import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { Search, Shield } from "lucide-react";
import api from "@/lib/api";
import { PageHeader } from "@/components/PageHeader";
import { Avatar, LevelBadge } from "@/components/common";

export default function Members() {
  const [q, setQ] = useState("");
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async (query) => {
    setLoading(true);
    const res = await api.get("/members", { params: query ? { q: query } : {} });
    setMembers(res.data);
    setLoading(false);
  }, []);

  useEffect(() => {
    const t = setTimeout(() => load(q), 250);
    return () => clearTimeout(t);
  }, [q, load]);

  return (
    <div className="space-y-8">
      <PageHeader eyebrow="Directory" title="Members"
        subtitle="Find peers across the guild by name, role, or expertise." />

      <div className="flex items-center gap-3 border border-border bg-card rounded-xl px-4 py-3">
        <Search size={16} className="text-muted-foreground" />
        <input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search by name, title, or bio…"
          data-testid="member-search-input"
          className="flex-1 bg-transparent outline-none text-sm"
        />
        <span className="text-xs font-mono text-muted-foreground">{members.length}</span>
      </div>

      {loading ? (
        <div className="text-center py-16 text-muted-foreground eyebrow">Loading…</div>
      ) : members.length === 0 ? (
        <div className="text-center py-16 border border-dashed border-border text-muted-foreground">
          No members match “{q}”.
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {members.map((m) => (
            <Link key={m.user_id} to={`/members/${m.user_id}`}
              data-testid={`member-card-${m.user_id}`}
              className="group border border-border bg-card rounded-xl p-5 hover:border-gold transition-colors flex flex-col">
              <div className="flex items-center gap-3">
                <Avatar src={m.picture} name={m.name} size={52} />
                <div className="min-w-0">
                  <div className="font-semibold text-sm flex items-center gap-1.5 group-hover:text-gold transition-colors">
                    {m.name} {m.is_admin && <Shield size={12} className="text-gold" />}
                  </div>
                  {m.title && <div className="text-xs text-muted-foreground truncate">{m.title}</div>}
                </div>
              </div>
              {m.bio && <p className="mt-3 text-sm text-muted-foreground leading-relaxed line-clamp-2 flex-1">{m.bio}</p>}
              <div className="mt-4 flex items-center justify-between">
                <LevelBadge level={m.level} />
                <span className="text-xs font-mono text-muted-foreground">{m.points.toLocaleString()} pts</span>
              </div>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}
